import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/lib/stores/auth-store';
import { getDashboardEnv } from '@/lib/env';

/**
 * Premium login page — clean light aesthetic.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { signIn, login, setError, loading, error } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const { supabaseUrl } = getDashboardEnv();

    if (supabaseUrl) {
      await signIn(email, password);
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) navigate('/visits');
    } else {
      const doctorId = new URLSearchParams(window.location.search).get('doctor') || 'doctor-001';
      login(email, 'mock-token', doctorId);
      navigate('/visits');
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden gradient-bg px-4">
      <div className="relative w-full max-w-[380px] animate-reveal">
        <div className="relative rounded-2xl border border-slate-200/80 bg-white p-9 shadow-xl ring-1 ring-teal-200/20">
          {/* Logo + Branding */}
          <div className="mb-10 flex flex-col items-center">
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-2xl bg-teal-100/30 animate-breathe" />
              <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/60 ring-1 ring-teal-200/50 shadow-sm">
                <svg className="h-7 w-7 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
              </div>
            </div>
            <h1 className="font-serif text-[1.75rem] text-slate-900">
              AssistMD
            </h1>
            <p className="overline mt-2 text-teal-600/60">
              Clinical Intelligence
            </p>
          </div>

          <div className="divider-gradient mb-8" />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="email" className="text-2xs font-semibold uppercase tracking-[0.1em] text-slate-400">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="clinician@example.com"
                autoComplete="email"
                className="input-subtle w-full"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-2xs font-semibold uppercase tracking-[0.1em] text-slate-400">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                className="input-subtle w-full"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50/80 px-3 py-2.5 text-xs text-red-600">
                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              loading={loading}
              className="w-full"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Secure · HIPAA Compliant · SOC 2
          </p>
        </div>
      </div>
    </div>
  );
}
